import {
    type AppDefaultInfo,
    type BuilderInfo,
    type EnvironmentInfo,
    type UserInfo,
} from "helpers/AppProvider.types";

import { type BTLoginTypes } from "types/enum";

import { type IRouteLocationState } from "utilities/flatRoutes/usePreviousLocation/usePreviousLocation.types";

import { BTExternalLink } from "commonComponents/btWrappers/BTExternalLink/BTExternalLink";
import { BTIconExternalLink } from "commonComponents/btWrappers/BTIcon";
import { BTLink, BTLinkRelative } from "commonComponents/btWrappers/BTLink/BTLink";
import {
    type MainNavigationResponse,
    type NavMenuItemResponse,
} from "commonComponents/utilities/MainNavigation/MainNavigation.api.types";
import {
    type IMenuTabInfo,
    type IMenuTabInfoLinkUrlInfo,
    MenuOnClickTypes,
} from "commonComponents/utilities/MainNavigation/MainNavigation.types";

interface IMainNavLinkProps {
    info: IMenuTabInfo;
    data: MainNavigationResponse;
    navMenuItem: NavMenuItemResponse;
    jobId: number;
    isTemplateMode: boolean;
    loginType: BTLoginTypes;
    "data-testid": string;
    onContentClick: (info: IMenuTabInfo, navMenuItem: NavMenuItemResponse) => void;
    builderInfo?: BuilderInfo;
    userInfo: UserInfo;
    appDefaultInfo?: AppDefaultInfo;
    environmentInfo?: EnvironmentInfo;
    children: React.ReactNode | string;
    removeTabIndex?: boolean; // for special cases where we want to remove tabIndex (e.g. parent of a submenu)
    quickAddLink?: IRouteLocationState | string;
    id?: string; // preserve legacy anchor id usage (Needed for qualtrics link don't remove unless we changed how that works)
}

interface IResolvedMainNavLink {
    onClickType: MenuOnClickTypes;
    url: string | IRouteLocationState; // For NoLink this is a placeholder
}

/**
 * Single source of truth for resolving a nav link's target + click type.
 *
 * @param info - Menu tab information containing click type resolver and URL getter functions
 * @param linkUrlInfo - Context data needed to resolve the link URL, including login type, job ID, builder info, etc.
 * @param builderInfo - Optional builder-specific information used for conditional link resolution
 * @param userInfo - Optional user-specific information used for conditional link resolution
 * @param options - Optional configuration object for fallback URLs
 * @param options.anchorFallback - Fallback URL for anchor links (defaults to "#")
 * @param options.internalFallback - Fallback URL for internal navigation links (defaults to "/")
 * @returns Resolved link object containing the click type and target URL/route
 */
function resolveMainNavLink(
    info: IMenuTabInfo,
    linkUrlInfo: IMenuTabInfoLinkUrlInfo,
    builderInfo?: BuilderInfo,
    userInfo?: UserInfo,
    options?: { anchorFallback?: string; internalFallback?: string },
): IResolvedMainNavLink {
    const anchorFallback = options?.anchorFallback ?? "#";
    const internalFallback = options?.internalFallback ?? "/";

    const onClickType =
        info.onClickType?.(builderInfo, userInfo, linkUrlInfo) ?? MenuOnClickTypes.Anchor;

    let resolved: string | IRouteLocationState | undefined = undefined;
    if (info.getLinkUrl) {
        resolved = info.getLinkUrl(linkUrlInfo);
    }

    switch (onClickType) {
        case MenuOnClickTypes.BTLink:
        case MenuOnClickTypes.BTLinkRelative:
            return { onClickType, url: resolved ?? internalFallback };
        case MenuOnClickTypes.NoLink:
            return { onClickType, url: anchorFallback };
        case MenuOnClickTypes.External:
            return { onClickType, url: (resolved as string) ?? anchorFallback };
        default:
            return {
                onClickType: MenuOnClickTypes.Anchor,
                url: (resolved as string) ?? anchorFallback,
            };
    }
}

const MAIN_LINK_CLASS = "MainNavMenuItemAction";

export const MainNavLink: React.FunctionComponent<IMainNavLinkProps> = ({
    id,
    info,
    data,
    navMenuItem,
    jobId,
    isTemplateMode,
    loginType,
    onContentClick,
    builderInfo,
    appDefaultInfo,
    environmentInfo,
    children,
    userInfo,
    quickAddLink,
    ...props
}) => {
    const linkUrlInfo: IMenuTabInfoLinkUrlInfo = {
        loginType,
        data,
        jobId,
        isTemplateMode,
        builderInfo,
        userInfo,
        appDefaultInfo,
        environmentInfo,
    };

    let { onClickType, url } = resolveMainNavLink(info, linkUrlInfo, builderInfo, userInfo);
    if (quickAddLink) {
        url = quickAddLink;
        const isModalRoute =
            typeof quickAddLink === "string"
                ? quickAddLink.startsWith("/QuickAction")
                : quickAddLink.pathname?.startsWith("/QuickAction");
        onClickType = isModalRoute ? MenuOnClickTypes.BTLinkRelative : MenuOnClickTypes.BTLink;
    }
    const metaProps = {
        "data-testid": props["data-testid"],
        id,
    };

    switch (onClickType) {
        case MenuOnClickTypes.Anchor:
        case MenuOnClickTypes.NoLink:
            return (
                <a
                    href={url as string}
                    className={MAIN_LINK_CLASS}
                    onClick={() => onContentClick(info, navMenuItem)}
                    {...metaProps}
                    {...(props.removeTabIndex ? { tabIndex: -1 } : {})}
                >
                    {children}
                </a>
            );
        case MenuOnClickTypes.BTLinkRelative:
            return (
                <BTLinkRelative
                    to={url}
                    className={MAIN_LINK_CLASS}
                    onClick={() => onContentClick(info, navMenuItem)}
                    {...metaProps}
                >
                    {children}
                </BTLinkRelative>
            );
        case MenuOnClickTypes.BTLink:
            return (
                <BTLink
                    to={url}
                    className={MAIN_LINK_CLASS}
                    onClick={() => onContentClick(info, navMenuItem)}
                    useAutoSPARouting
                    {...metaProps}
                >
                    {children}
                </BTLink>
            );
        case MenuOnClickTypes.External:
            return (
                <div className="flex flex-row gap-0.5">
                    <BTExternalLink
                        href={url as string}
                        className={MAIN_LINK_CLASS}
                        onClick={() => onContentClick(info, navMenuItem)}
                        {...metaProps}
                    >
                        {children}
                    </BTExternalLink>
                    <BTIconExternalLink />
                </div>
            );
        default:
            return null;
    }
};
